/**
 * ShieldWall — Argon2id WASM Loader
 *
 * Loads a WASM Argon2id module for memory-hard PoW. Ships with graceful
 * fallback: when WASM is unavailable (old browsers, blocked by CSP, missing
 * cross-origin isolation headers for SharedArrayBuffer, or the module
 * simply hasn't been bundled yet) the caller falls back to the SHA-256
 * chain solver in pow.ts. The server accepts both algorithms — the choice
 * is bound into the HMAC v2 canonical.
 *
 * SW-116: prefers a Web Worker so the main thread stays responsive; falls
 * back to main-thread yielding when Worker is unavailable (jsdom, CSP, etc.).
 *
 * @module pow-argon2
 */
import { argon2id } from "hash-wasm";
import type { PoWResult } from "./pow";

export interface Argon2Config {
  memoryKiB: number;
  iterations: number;
  parallelism: number;
  maxSolveTimeMs: number;
  yieldInterval: number;
  /** When false, skip Worker and solve on the calling thread. Default true. */
  preferWorker?: boolean;
}

export const DEFAULT_ARGON2_CONFIG: Argon2Config = {
  memoryKiB: 4096,
  iterations: 2,
  parallelism: 1,
  maxSolveTimeMs: 1500,
  yieldInterval: 5,
  preferWorker: true,
};

export type Argon2PoWResult = Omit<PoWResult, "type" | "memoryBlocks"> & {
  type: "argon2id";
  memoryKiB: number;
};

/**
 * Feature-detect whether the environment can run Argon2id. hash-wasm bundles
 * its own WASM (no SharedArrayBuffer / cross-origin isolation needed), so this
 * is effectively "WebAssembly available" — true in every modern browser.
 */
export function isArgon2Supported(): boolean {
  return typeof WebAssembly !== "undefined";
}

async function argon2Hex(challenge: string, powNonce: number, cfg: Argon2Config): Promise<string> {
  return argon2id({
    password: `${challenge}:${powNonce}`,
    salt: challenge,
    parallelism: cfg.parallelism,
    iterations: cfg.iterations,
    memorySize: cfg.memoryKiB,
    hashLength: 32,
    outputType: "hex",
  });
}

/** Main-thread solver with time-budget yielding (pre-SW-116 path). */
export async function solveArgon2PoWMainThread(
  challenge: string,
  difficulty: number,
  onProgress?: (percent: number) => void,
  config: Partial<Argon2Config> = {}
): Promise<Argon2PoWResult | null> {
  if (!isArgon2Supported()) return null;

  const cfg = { ...DEFAULT_ARGON2_CONFIG, ...config };
  const prefix = "0".repeat(difficulty);
  const start = performance.now();
  const deadline = cfg.maxSolveTimeMs * 3;
  const YIELD_MS = 12;
  let nonce = 0;
  let lastYield = start;

  while (performance.now() - start < deadline) {
    const hashHex = await argon2Hex(challenge, nonce, cfg);

    if (hashHex.startsWith(prefix)) {
      return {
        nonce,
        hash: hashHex,
        solveTimeMs: Math.round(performance.now() - start),
        iterations: nonce + 1,
        memoryKiB: cfg.memoryKiB,
        type: "argon2id",
      };
    }

    nonce++;
    const now = performance.now();
    if (now - lastYield >= YIELD_MS) {
      onProgress?.(Math.min(((now - start) / cfg.maxSolveTimeMs) * 90, 95));
      await new Promise((r) => setTimeout(r, 0));
      lastYield = performance.now();
    }
  }

  throw new Error(
    `Argon2id PoW timeout: exceeded ${deadline}ms after ${nonce} iterations`
  );
}

let _reqId = 0;

function tryCreateArgon2Worker(): Worker | null {
  if (typeof Worker === "undefined") return null;
  try {
    // Vite resolves this to a worker bundle; fails cleanly in plain Node/jsdom.
    return new Worker(new URL("./pow-argon2-worker.ts", import.meta.url), {
      type: "module",
    });
  } catch {
    return null;
  }
}

async function solveArgon2PoWInWorker(
  challenge: string,
  difficulty: number,
  cfg: Argon2Config,
  onProgress?: (percent: number) => void
): Promise<Argon2PoWResult | null> {
  const worker = tryCreateArgon2Worker();
  if (!worker) return null;

  const id = ++_reqId;
  onProgress?.(5);

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      worker.terminate();
      reject(new Error(`Argon2id PoW worker timeout after ${cfg.maxSolveTimeMs * 3}ms`));
    }, cfg.maxSolveTimeMs * 3 + 1000);

    worker.onmessage = (ev: MessageEvent) => {
      if (ev.data?.id !== id) return;
      clearTimeout(timer);
      worker.terminate();
      if (ev.data.ok) {
        onProgress?.(100);
        resolve(ev.data.result as Argon2PoWResult);
      } else {
        reject(new Error(ev.data.error ?? "Argon2id worker failed"));
      }
    };
    worker.onerror = (err) => {
      clearTimeout(timer);
      worker.terminate();
      reject(err.error ?? new Error("Argon2id worker error"));
    };

    worker.postMessage({
      id,
      challenge,
      difficulty,
      config: {
        memoryKiB: cfg.memoryKiB,
        iterations: cfg.iterations,
        parallelism: cfg.parallelism,
        maxSolveTimeMs: cfg.maxSolveTimeMs,
      },
    });
  });
}

/**
 * Solve an Argon2id PoW challenge (SW-115 + SW-116 Worker). Prefers a Web
 * Worker; falls back to main-thread yielding. Returns null only if WebAssembly
 * is unavailable (caller then falls back to the SHA chain).
 */
export async function solveArgon2PoW(
  challenge: string,
  difficulty: number,
  onProgress?: (percent: number) => void,
  config: Partial<Argon2Config> = {}
): Promise<Argon2PoWResult | null> {
  if (!isArgon2Supported()) return null;

  const cfg = { ...DEFAULT_ARGON2_CONFIG, ...config };
  if (cfg.preferWorker !== false) {
    try {
      const workerResult = await solveArgon2PoWInWorker(
        challenge,
        difficulty,
        cfg,
        onProgress
      );
      if (workerResult) return workerResult;
    } catch {
      // Worker unavailable or failed — fall through to main thread.
    }
  }

  return solveArgon2PoWMainThread(challenge, difficulty, onProgress, {
    ...config,
    preferWorker: false,
  });
}
